import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, 
  Download, 
  Users, 
  Calendar, 
  Clock, 
  Shield, 
  AlertTriangle,
  TrendingUp,
  FileSpreadsheet,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('statistiche');
  
  // Filters
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const [anno, setAnno] = useState(new Date().getFullYear().toString());
  const [giorniScadenza, setGiorniScadenza] = useState('30');
  
  // Report data
  const [statistiche, setStatistiche] = useState(null);
  const [orePerPeriodo, setOrePerPeriodo] = useState(null);
  const [presenzeEventi, setPresenzeEventi] = useState(null);
  const [senzaCopertura, setSenzaCopertura] = useState(null);
  const [copertureScadenza, setCopertureScadenza] = useState(null);

  const fetchWithAuth = async (url) => {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Errore nel caricamento dati');
    return response.json();
  };

  // Load statistiche generali on mount
  useEffect(() => {
    loadStatistiche();
  }, [anno]);

  const loadStatistiche = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/reports/statistiche-generali?anno=${anno}`);
      setStatistiche(data);
    } catch (error) {
      toast.error('Errore nel caricamento statistiche');
    } finally {
      setLoading(false);
    }
  };

  const loadOrePerPeriodo = async () => {
    if (!dataInizio || !dataFine) {
      toast.error('Seleziona le date di inizio e fine periodo');
      return;
    }
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/reports/ore-per-periodo?data_inizio=${dataInizio}&data_fine=${dataFine}`);
      setOrePerPeriodo(data);
    } catch (error) {
      toast.error('Errore nel caricamento report ore');
    } finally {
      setLoading(false);
    }
  };

  const loadPresenzeEventi = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/reports/presenze-per-evento`;
      const params = [];
      if (dataInizio) params.push(`data_inizio=${dataInizio}`);
      if (dataFine) params.push(`data_fine=${dataFine}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const data = await fetchWithAuth(url);
      setPresenzeEventi(data);
    } catch (error) {
      toast.error('Errore nel caricamento report presenze');
    } finally {
      setLoading(false);
    }
  };

  const loadSenzaCopertura = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/reports/volontari-senza-copertura`);
      setSenzaCopertura(data);
    } catch (error) {
      toast.error('Errore nel caricamento report coperture');
    } finally {
      setLoading(false);
    }
  };

  const loadCopertureScadenza = async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/reports/coperture-in-scadenza?giorni=${giorniScadenza}`);
      setCopertureScadenza(data);
    } catch (error) {
      toast.error('Errore nel caricamento coperture in scadenza');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async (tipo) => {
    try {
      let url = `${API_URL}/api/reports/export-csv/${tipo}`;
      const params = [];
      if (dataInizio) params.push(`data_inizio=${dataInizio}`);
      if (dataFine) params.push(`data_fine=${dataFine}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Errore export');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${tipo}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Report e Statistiche
          </h1>
          <p className="text-slate-500 mt-1">
            Analisi dati e export per la tua associazione
          </p>
        </div>
        
        {/* Quick Export Buttons */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportCSV('volontari')} data-testid="export-volontari-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Volontari CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportCSV('eventi')} data-testid="export-eventi-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Eventi CSV
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-6">
          <TabsTrigger value="statistiche" data-testid="tab-statistiche">Statistiche</TabsTrigger>
          <TabsTrigger value="ore" data-testid="tab-ore">Ore Volontari</TabsTrigger>
          <TabsTrigger value="presenze" data-testid="tab-presenze">Presenze</TabsTrigger>
          <TabsTrigger value="coperture" data-testid="tab-coperture">Coperture</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export">Export</TabsTrigger>
        </TabsList>

        {/* STATISTICHE GENERALI */}
        <TabsContent value="statistiche">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#0055BB]" />
                    Statistiche Generali
                  </CardTitle>
                  <CardDescription>Panoramica dell'attività dell'ente</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label>Anno:</Label>
                  <Select value={anno} onValueChange={setAnno}>
                    <SelectTrigger className="w-32" data-testid="select-anno">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
                </div>
              ) : statistiche ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <Users className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="text-sm text-blue-600">Volontari Attivi</p>
                          <p className="text-2xl font-bold text-blue-700">{statistiche.volontari?.attivi || 0}</p>
                          <p className="text-xs text-blue-500">su {statistiche.volontari?.totali || 0} totali</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-8 h-8 text-green-600" />
                        <div>
                          <p className="text-sm text-green-600">Eventi {anno}</p>
                          <p className="text-2xl font-bold text-green-700">{statistiche.eventi?.totali_anno || 0}</p>
                          <p className="text-xs text-green-500">{statistiche.eventi?.conclusi || 0} conclusi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <Clock className="w-8 h-8 text-purple-600" />
                        <div>
                          <p className="text-sm text-purple-600">Ore Donate {anno}</p>
                          <p className="text-2xl font-bold text-purple-700">{statistiche.presenze?.ore_totali_anno || 0}</p>
                          <p className="text-xs text-purple-500">{statistiche.presenze?.totali_anno || 0} presenze</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <Shield className="w-8 h-8 text-amber-600" />
                        <div>
                          <p className="text-sm text-amber-600">Coperture Attive</p>
                          <p className="text-2xl font-bold text-amber-700">{statistiche.coperture?.attive || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">Nessun dato disponibile</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ORE VOLONTARI */}
        <TabsContent value="ore">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#0055BB]" />
                Report Ore per Periodo
              </CardTitle>
              <CardDescription>Ore di volontariato per volontario nel periodo selezionato</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Label>Data Inizio</Label>
                  <Input
                    type="date"
                    value={dataInizio}
                    onChange={(e) => setDataInizio(e.target.value)}
                    data-testid="input-data-inizio"
                  />
                </div>
                <div className="flex-1">
                  <Label>Data Fine</Label>
                  <Input
                    type="date"
                    value={dataFine}
                    onChange={(e) => setDataFine(e.target.value)}
                    data-testid="input-data-fine"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={loadOrePerPeriodo} disabled={loading} data-testid="btn-carica-ore">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                    Genera Report
                  </Button>
                  {orePerPeriodo && (
                    <Button variant="outline" onClick={() => exportCSV('ore-volontari')} data-testid="btn-export-ore">
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                  )}
                </div>
              </div>

              {orePerPeriodo && (
                <div>
                  <div className="flex gap-4 mb-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Periodo</p>
                      <p className="font-medium">{orePerPeriodo.periodo?.inizio} - {orePerPeriodo.periodo?.fine}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Ore Totali</p>
                      <p className="font-medium text-[#0055BB]">{orePerPeriodo.totale_ore}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Presenze Totali</p>
                      <p className="font-medium">{orePerPeriodo.totale_presenze}</p>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="px-4 py-3 text-left">Volontario</th>
                          <th className="px-4 py-3 text-left">Stato</th>
                          <th className="px-4 py-3 text-right">Presenze</th>
                          <th className="px-4 py-3 text-right">Ore Totali</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orePerPeriodo.volontari?.map((v, i) => (
                          <tr key={i} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium">{v.nome}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                v.stato === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                              }`}>
                                {v.stato}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">{v.presenze}</td>
                            <td className="px-4 py-3 text-right font-medium text-[#0055BB]">{v.ore_totali}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PRESENZE PER EVENTO */}
        <TabsContent value="presenze">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#0055BB]" />
                Report Presenze per Evento
              </CardTitle>
              <CardDescription>Partecipazione e ore per ogni evento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Label>Data Inizio (opzionale)</Label>
                  <Input
                    type="date"
                    value={dataInizio}
                    onChange={(e) => setDataInizio(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <Label>Data Fine (opzionale)</Label>
                  <Input
                    type="date"
                    value={dataFine}
                    onChange={(e) => setDataFine(e.target.value)}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={loadPresenzeEventi} disabled={loading} data-testid="btn-carica-presenze">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                    Genera Report
                  </Button>
                  {presenzeEventi && (
                    <Button variant="outline" onClick={() => exportCSV('presenze')} data-testid="btn-export-presenze">
                      <Download className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                  )}
                </div>
              </div>

              {presenzeEventi && (
                <div>
                  <div className="flex gap-4 mb-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Eventi Totali</p>
                      <p className="font-medium">{presenzeEventi.totale_eventi}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Partecipazioni Totali</p>
                      <p className="font-medium">{presenzeEventi.totale_partecipanti}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Ore Totali</p>
                      <p className="font-medium text-[#0055BB]">{presenzeEventi.totale_ore}</p>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="px-4 py-3 text-left">Evento</th>
                          <th className="px-4 py-3 text-left">Data</th>
                          <th className="px-4 py-3 text-left">Luogo</th>
                          <th className="px-4 py-3 text-left">Stato</th>
                          <th className="px-4 py-3 text-right">Partecipanti</th>
                          <th className="px-4 py-3 text-right">Ore</th>
                        </tr>
                      </thead>
                      <tbody>
                        {presenzeEventi.eventi?.map((e, i) => (
                          <tr key={i} className="border-b hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium">{e.titolo}</td>
                            <td className="px-4 py-3">{e.data}</td>
                            <td className="px-4 py-3 text-slate-600">{e.luogo}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                e.stato === 'concluso' ? 'bg-green-100 text-green-700' :
                                e.stato === 'programmato' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {e.stato}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">{e.num_partecipanti}</td>
                            <td className="px-4 py-3 text-right font-medium text-[#0055BB]">{e.ore_totali}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COPERTURE */}
        <TabsContent value="coperture">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Volontari senza copertura */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Volontari Senza Copertura
                </CardTitle>
                <CardDescription>Volontari attivi senza copertura assicurativa</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={loadSenzaCopertura} disabled={loading} className="mb-4" data-testid="btn-senza-copertura">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                  Verifica
                </Button>
                
                {senzaCopertura && (
                  <div>
                    <p className="text-sm text-slate-500 mb-3">
                      Trovati <span className="font-bold text-red-600">{senzaCopertura.totale}</span> volontari senza copertura
                    </p>
                    {senzaCopertura.volontari_senza_copertura?.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {senzaCopertura.volontari_senza_copertura.map((v, i) => (
                          <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="font-medium text-red-800">{v.nome}</p>
                            <p className="text-xs text-red-600">{v.codice_fiscale}</p>
                            {v.telefono && <p className="text-xs text-slate-600">{v.telefono}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-green-600 text-center py-4">Tutti i volontari hanno una copertura attiva!</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Coperture in scadenza */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-600">
                  <Clock className="w-5 h-5" />
                  Coperture in Scadenza
                </CardTitle>
                <CardDescription>Coperture che scadranno a breve</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Select value={giorniScadenza} onValueChange={setGiorniScadenza}>
                    <SelectTrigger className="w-40" data-testid="select-giorni">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 giorni</SelectItem>
                      <SelectItem value="15">15 giorni</SelectItem>
                      <SelectItem value="30">30 giorni</SelectItem>
                      <SelectItem value="60">60 giorni</SelectItem>
                      <SelectItem value="90">90 giorni</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadCopertureScadenza} disabled={loading} data-testid="btn-coperture-scadenza">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                    Verifica
                  </Button>
                </div>
                
                {copertureScadenza && (
                  <div>
                    <p className="text-sm text-slate-500 mb-3">
                      Trovate <span className="font-bold text-amber-600">{copertureScadenza.totale}</span> coperture in scadenza
                    </p>
                    {copertureScadenza.coperture_in_scadenza?.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {copertureScadenza.coperture_in_scadenza.map((c, i) => (
                          <div key={i} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="font-medium text-amber-800">{c.volontario_nome}</p>
                            <div className="flex justify-between text-xs">
                              <span className="text-slate-600">Polizza: {c.numero_polizza || 'N/D'}</span>
                              <span className="text-amber-600 font-medium">Scade: {c.scadenza}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-green-600 text-center py-4">Nessuna copertura in scadenza nel periodo selezionato</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* EXPORT */}
        <TabsContent value="export">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#0055BB]" />
                Export Dati
              </CardTitle>
              <CardDescription>Scarica i dati in formato CSV per analisi esterne</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <Label>Data Inizio (opzionale per filtro)</Label>
                  <Input
                    type="date"
                    value={dataInizio}
                    onChange={(e) => setDataInizio(e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <Label>Data Fine (opzionale per filtro)</Label>
                  <Input
                    type="date"
                    value={dataFine}
                    onChange={(e) => setDataFine(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Button 
                  variant="outline" 
                  className="h-24 flex flex-col items-center justify-center gap-2"
                  onClick={() => exportCSV('volontari')}
                  data-testid="export-btn-volontari"
                >
                  <Users className="w-8 h-8 text-[#0055BB]" />
                  <span>Volontari</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="h-24 flex flex-col items-center justify-center gap-2"
                  onClick={() => exportCSV('eventi')}
                  data-testid="export-btn-eventi"
                >
                  <Calendar className="w-8 h-8 text-[#0055BB]" />
                  <span>Eventi</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="h-24 flex flex-col items-center justify-center gap-2"
                  onClick={() => exportCSV('presenze')}
                  data-testid="export-btn-presenze"
                >
                  <Clock className="w-8 h-8 text-[#0055BB]" />
                  <span>Presenze</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="h-24 flex flex-col items-center justify-center gap-2"
                  onClick={() => exportCSV('ore-volontari')}
                  data-testid="export-btn-ore"
                >
                  <BarChart3 className="w-8 h-8 text-[#0055BB]" />
                  <span>Ore Volontari</span>
                </Button>
              </div>

              <p className="text-xs text-slate-500 mt-4 text-center">
                I file CSV possono essere aperti con Excel, Google Sheets o altri software di foglio elettronico.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
